import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Modal } from '@/shared/components/ui/Modal'

interface KpiCardProps {
  label: string
  value: string | number
  unit?: string
  trend?: number
  trendLabel?: string
  accentColor?: string
  clickable?: boolean
  onClick?: () => void
  loading?: boolean
  className?: string
  /** @deprecated Use detailNode instead */
  detail?: string
  /** Rich content shown in a modal overlay on click */
  detailNode?: React.ReactNode
  /** Title for the detail modal (defaults to label) */
  detailTitle?: string
  variant?: string
}

export function KpiCard({ label, value, unit, trend, trendLabel, accentColor = '#C9A84C', clickable, onClick, loading, className, detail, detailNode, detailTitle }: KpiCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const hasModal = !!detailNode || !!detail
  const isInteractive = clickable || hasModal

  const handleClick = () => {
    if (hasModal) setModalOpen(true)
    else if (onClick) onClick()
  }

  return (
    <>
      <div
        className={cn(
          'relative bg-white rounded-card border border-ink-border p-5 shadow-card hover:shadow-elevated hover:-translate-y-px transition-all overflow-hidden',
          isInteractive && 'cursor-pointer',
          className,
        )}
        onClick={isInteractive ? handleClick : undefined}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
      >
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-card" style={{ background: accentColor }} />

        <div className="relative z-10">
          <p className="text-[9px] font-medium tracking-[0.14em] uppercase text-ink-faint mt-1">{label}</p>

          {loading ? (
            <div className="h-9 w-24 bg-paper-warm rounded animate-pulse mt-2" />
          ) : (
            <p className="font-serif text-[22px] font-bold text-ink mt-2 leading-tight">
              {value}
              {unit && <span className="font-sans text-xs font-normal text-ink-muted ml-1">{unit}</span>}
            </p>
          )}

          {trend !== undefined && !loading && (
            <div className="flex items-center gap-1.5 mt-3">
              <span className={cn(
                'text-[10px] font-medium px-2 py-0.5 rounded-full',
                trend >= 0 ? 'text-teal-dark bg-teal-light' : 'text-brick bg-brick-light',
              )}>
                {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
              </span>
              {trendLabel && <span className="text-[10px] text-ink-faint">{trendLabel}</span>}
            </div>
          )}
        </div>

        {clickable && !hasModal && (
          <ChevronRight size={13} className="absolute top-5 right-4 text-ink-faint" />
        )}
      </div>

      {/* Detail modal */}
      {hasModal && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={detailTitle || label}>
          {detailNode || <p className="text-[13px] text-ink-muted leading-relaxed">{detail}</p>}
        </Modal>
      )}
    </>
  )
}
